import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import { dashboardService } from '../../services/dashboardService';
import {
  DashboardStats,
  AppointmentChartData,
  RevenueChartData,
  SpeciesData,
  RecentActivity
} from '../../services/dashboardService';

interface ChartData {
  appointments: AppointmentChartData[];
  revenue: RevenueChartData[];
  species: SpeciesData[];
}

const COLORS = ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4'];

type CardTheme = 'indigo' | 'amber' | 'emerald' | 'rose' | 'violet' | 'cyan';

const cardThemes: Record<CardTheme, { gradient: string; iconBg: string; indicator: string }> = {
  indigo: {
    gradient: 'from-brand-primary/25 via-brand-primary/10 to-white',
    iconBg: 'bg-brand-primary/15 text-brand-primary',
    indicator: 'bg-brand-primary/50'
  },
  amber: {
    gradient: 'from-amber-200/50 via-orange-100 to-white',
    iconBg: 'bg-amber-100 text-amber-600',
    indicator: 'bg-amber-400/60'
  },
  emerald: {
    gradient: 'from-emerald-200/50 via-emerald-100 to-white',
    iconBg: 'bg-emerald-100 text-emerald-600',
    indicator: 'bg-emerald-400/60'
  },
  rose: {
    gradient: 'from-rose-200/50 via-rose-100 to-white',
    iconBg: 'bg-rose-100 text-rose-500',
    indicator: 'bg-rose-400/60'
  },
  violet: {
    gradient: 'from-brand-secondary/30 via-violet-100 to-white',
    iconBg: 'bg-violet-100 text-brand-secondary',
    indicator: 'bg-brand-secondary/50'
  },
  cyan: {
    gradient: 'from-brand-accent/25 via-cyan-100 to-white',
    iconBg: 'bg-cyan-100 text-brand-accent',
    indicator: 'bg-brand-accent/50'
  }
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalPatients: 0,
    todayAppointments: 0,
    pendingInvoices: 0,
    lowStockItems: 0,
    activeUsers: 0,
    monthlyRevenue: 0,
    newPatientsThisMonth: 0,
    completedAppointmentsThisMonth: 0
  });
  const [chartData, setChartData] = useState<ChartData>({
    appointments: [],
    revenue: [],
    species: []
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load all dashboard data in parallel
        const [
          statsResponse,
          appointmentsResponse,
          revenueResponse,
          speciesResponse,
          activityResponse
        ] = await Promise.all([
          dashboardService.getDashboardStats(),
          dashboardService.getAppointmentChart('month'),
          dashboardService.getRevenueChart('month'),
          dashboardService.getSpeciesDistribution(),
          dashboardService.getRecentActivity(5)
        ]);

        if (statsResponse.success && statsResponse.data) {
          setStats(statsResponse.data);
        }

        if (appointmentsResponse.success && appointmentsResponse.data) {
          setChartData(prev => ({ ...prev, appointments: appointmentsResponse.data || [] }));
        }

        if (revenueResponse.success && revenueResponse.data) {
          setChartData(prev => ({ ...prev, revenue: revenueResponse.data || [] }));
        }

        if (speciesResponse.success && speciesResponse.data) {
          setChartData(prev => ({ ...prev, species: speciesResponse.data || [] }));
        }

        if (activityResponse.success && activityResponse.data) {
          setRecentActivity(activityResponse.data);
        }

      } catch (err: any) {
        console.error('Failed to load dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');

        // Fallback to mock data if API fails
        setStats({
          totalPatients: 2,
          todayAppointments: 3,
          pendingInvoices: 2,
          lowStockItems: 1,
          activeUsers: 4,
          monthlyRevenue: 324500,
          newPatientsThisMonth: 2,
          completedAppointmentsThisMonth: 1
        });

        setChartData({
          appointments: [
            { date: 'Jan 1', appointments: 2, completed: 2 },
            { date: 'Jan 8', appointments: 1, completed: 0 },
            { date: 'Jan 15', appointments: 3, completed: 1 },
            { date: 'Jan 20', appointments: 1, completed: 0 },
            { date: 'Jan 25', appointments: 1, completed: 0 }
          ],
          revenue: [
            { month: 'Jan', revenue: 324500, appointments: 3 },
            { month: 'Feb', revenue: 0, appointments: 0 },
            { month: 'Mar', revenue: 0, appointments: 0 }
          ],
          species: [
            { species: 'Dog', count: 1, percentage: 50 },
            { species: 'Cat', count: 1, percentage: 50 }
          ]
        });

        setRecentActivity([
          {
            id: '1',
            type: 'appointment',
            title: 'Appointment scheduled for Max',
            timestamp: new Date().toISOString(),
            user: { name: 'Dr. Sarah Johnson' }
          }
        ]);

      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const dashboardCards = [
    {
      title: 'Total Patients',
      value: stats.totalPatients,
      icon: '🐾',
      theme: 'indigo' as CardTheme,
      link: '/patients',
      description: 'Registered patients'
    },
    {
      title: "Today's Appointments",
      value: stats.todayAppointments,
      icon: '📅',
      theme: 'amber' as CardTheme,
      link: '/appointments',
      description: 'Scheduled for today'
    },
    {
      title: 'Pending Invoices',
      value: stats.pendingInvoices,
      icon: '💳',
      theme: 'emerald' as CardTheme,
      link: '/invoices',
      description: 'Awaiting payment'
    },
    {
      title: 'Low Stock Items',
      value: stats.lowStockItems,
      icon: '📦',
      theme: 'rose' as CardTheme,
      link: '/inventory',
      description: 'Require restocking soon'
    },
    {
      title: 'Monthly Revenue',
      value: stats.monthlyRevenue,
      format: 'currency' as const,
      icon: '📈',
      theme: 'cyan' as CardTheme,
      link: '/invoices',
      description: 'Collected this month'
    },
    {
      title: 'New Patients This Month',
      value: stats.newPatientsThisMonth,
      icon: '🆕',
      theme: 'violet' as CardTheme,
      link: '/patients',
      description: 'Joined this month'
    }
  ];

  const quickActions = [
    { title: 'Add New Patient', icon: '➕', link: '/patients', description: 'Create a new patient record.' },
    { title: 'Schedule Appointment', icon: '🗓️', link: '/appointments', description: 'Book a visit and reserve a vet.' },
    { title: 'Create Invoice', icon: '🧾', link: '/invoices', description: 'Generate billing for recent treatments.' },
    { title: 'Update Inventory', icon: '📋', link: '/inventory', description: 'Adjust stock and reorder supplies.' }
  ];

  if (loading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center rounded-3xl border border-slate-200 bg-white shadow-lg">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600" />
          <div>
            <p className="text-base font-semibold text-slate-800">Loading your clinic overview…</p>
            <p className="text-sm text-slate-500">Fetching the latest patient, revenue, and activity data.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-4 rounded-3xl border border-red-200 bg-red-50 p-6 shadow-lg">
        <div className="flex-1">
          <h3 className="font-bold text-red-800">Unable to load dashboard data</h3>
          <div className="mt-1 text-sm text-red-600">{error}</div>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <section className="grid gap-6 lg:grid-cols-[3fr,2fr]">
        <div className="relative min-h-[260px] overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-cyan-600 text-white shadow-2xl">
          <div className="absolute -left-10 -top-20 h-52 w-52 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -right-8 bottom-0 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex w-full flex-col items-start gap-6 p-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-4">
              <span className="inline-block rounded-full border border-white/40 bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em]">
                Overview
              </span>
              <div>
                <h1 className="text-3xl font-semibold leading-tight sm:text-4xl">Welcome back 👋</h1>
                <p className="mt-2 max-w-xl text-sm text-white/80">
                  Review today's schedule, track clinic performance, and keep an eye on what needs attention.
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/30 bg-white/10 p-5 shadow-lg backdrop-blur">
              <p className="text-sm text-white/70">Today is</p>
              <p className="mt-1 text-lg font-semibold">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg">
          <div className="space-y-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Quick Pulse</p>
              <p className="mt-2 text-3xl font-semibold text-slate-800">
                {stats.todayAppointments} appointments today
              </p>
              <p className="mt-3 text-sm text-slate-600">
                Keep an eye on arrivals and make sure treatment rooms are prepped ahead of time.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Pending invoices</p>
                <p className="mt-2 text-xl font-semibold text-slate-800">{stats.pendingInvoices}</p>
                <span className="mt-2 inline-block w-fit rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                  +2 new today
                </span>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Low stock items</p>
                <p className="mt-2 text-xl font-semibold text-slate-800">{stats.lowStockItems}</p>
                <span className="mt-2 inline-block w-fit rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">
                  Action required
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Clinic KPIs</h2>
            <p className="text-sm text-slate-600">Track the heartbeat of your clinic with live metrics.</p>
          </div>
          <Link
            to="/reports"
            className="inline-flex items-center gap-2 rounded-full border-2 border-indigo-600 px-4 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors"
          >
            View detailed reports
          </Link>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {dashboardCards.map((card) => {
            const theme = cardThemes[card.theme];
            const displayValue =
              typeof card.value === 'number'
                ? card.format === 'currency'
                  ? new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      maximumFractionDigits: 0
                    }).format(card.value)
                  : card.value.toLocaleString()
                : card.value;

            return (
              <Link
                key={card.title}
                to={card.link}
                className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-indigo-300 hover:shadow-lg"
              >
                <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${theme.gradient} opacity-0 transition-opacity duration-300 group-hover:opacity-80`} />
                <div className="relative flex items-start justify-between">
                  <div>
                    <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl text-lg shadow-sm ${theme.iconBg}`}>
                      {card.icon}
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">{card.title}</p>
                    <p className="mt-2 text-3xl font-semibold text-slate-800">{displayValue}</p>
                  </div>
                  <span className={`mt-1 h-14 w-1.5 rounded-full ${theme.indicator}`} />
                </div>
                <p className="mt-4 text-sm text-slate-600">{card.description}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600">
                  Explore <span aria-hidden="true">→</span>
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Charts Section */}
      <section className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Analytics & Insights</h2>
            <p className="text-sm text-slate-600">Visualize your clinic's performance and trends.</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Appointments Chart */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-slate-800">Appointment Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.appointments}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  fontSize={12}
                  tickLine={false}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={12}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="appointments"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={{ fill: '#6366f1', r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="completed"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 flex gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-indigo-600"></div>
                <span>Scheduled</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-emerald-600"></div>
                <span>Completed</span>
              </div>
            </div>
          </div>

          {/* Species Distribution Chart */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-slate-800">Patient Species</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.species as any}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry: any) => `${entry.species} ${entry.percentage}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {chartData.species.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue Chart */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold text-slate-800">Revenue Overview</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData.revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="month"
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
              />
              <YAxis
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
              />
              <Bar
                dataKey="revenue"
                fill="#6366f1"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[3fr,2fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-800">Quick actions</h2>
            <span className="inline-block rounded-full border border-slate-200 px-3 py-1 text-[0.65rem] uppercase tracking-[0.3em] text-slate-500">
              Productivity
            </span>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {quickActions.map((action) => (
              <Link
                key={action.title}
                to={action.link}
                className="group rounded-2xl border border-slate-200 bg-slate-50 p-5 transition hover:border-indigo-300 hover:bg-white hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <span className="text-2xl">{action.icon}</span>
                  <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700 transition group-hover:bg-indigo-600 group-hover:text-white">
                    Start
                  </span>
                </div>
                <p className="mt-3 text-sm font-semibold text-slate-800">{action.title}</p>
                <p className="mt-2 text-xs text-slate-600">{action.description}</p>
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-800">Recent activity</h2>
            <span className="inline-block rounded-full border border-slate-200 px-3 py-1 text-[0.65rem] uppercase tracking-[0.3em] text-slate-500">
              Live feed
            </span>
          </div>

          <ul className="mt-6 space-y-4">
            {recentActivity.map((activity) => (
              <li key={activity.id} className="flex gap-3">
                <div className="flex-shrink-0 text-lg">{activity.icon || '📋'}</div>
                <div className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-sm font-medium text-slate-800">{activity.title}</p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
                    {activity.time || new Date(activity.timestamp).toLocaleString()}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
